import { inject, injectable } from "inversify";
import { DependencyType } from "@di/DependencyType";
import {
  ApolloClient,
  NormalizedCacheObject,
} from "@apollo/client";
import {
  CreateLabResultDocument,
  CreateLabResultInput,
  CreateLabResultMutation,
  CreateLabResultMutationVariables,
  LabResultDocument,
  SoilSampleDocument,
  UpdateLabResultDocument,
  UpdateLabResultInput,
  UpdateLabResultMutation,
  UpdateLabResultMutationVariables,
} from "generated/graphql";

@injectable()
export class LabResultService {
  constructor(
    @inject(DependencyType.ApolloClient)
    private readonly apolloClient: ApolloClient<NormalizedCacheObject>
  ) {}

  async create(input: CreateLabResultInput) {
    const soilSampleId = input.soilSampleId?.trim();
    if (!soilSampleId) {
      throw new Error("soilSampleId is required.");
    }

    const mutationTuple = await this.apolloClient.mutate<
      CreateLabResultMutation,
      CreateLabResultMutationVariables
    >({
      mutation: CreateLabResultDocument,
      variables: {
        input,
      },
      refetchQueries: [
        {
          query: SoilSampleDocument,
          variables: {
            id: soilSampleId,
          },
        },
      ],
      awaitRefetchQueries: true,
    });

    if (mutationTuple.errors) {
      throw new Error(mutationTuple.errors[0].message);
    }

    return mutationTuple.data?.createLabResult;
  }

  async update(input: UpdateLabResultInput, soilSampleId: string) {
    const normalizedSoilSampleId = soilSampleId.trim();
    if (!normalizedSoilSampleId) {
      throw new Error("soilSampleId is required.");
    }

    const mutationTuple = await this.apolloClient.mutate<
      UpdateLabResultMutation,
      UpdateLabResultMutationVariables
    >({
      mutation: UpdateLabResultDocument,
      variables: {
        input,
      },
      refetchQueries: [
        {
          query: LabResultDocument,
          variables: {
            id: input.id,
          },
        },
        { query: SoilSampleDocument, variables: { id: normalizedSoilSampleId } },
      ],
      awaitRefetchQueries: true,
    });

    if (mutationTuple.errors) {
      throw new Error(mutationTuple.errors[0].message);
    }

    return mutationTuple.data?.updateLabResult;
  }
}
